import { useEffect, useState } from "react";
import { appConfig } from "./config";
import { ModelPicker } from "./ModelPicker";
import type { AgentMode } from "../api/types";
import { ModeSelector } from "../shared/ModeSelector";
import { ChatPanel } from "../chat/ChatPanel";
import { BafaWizard } from "../modes/bafa/BafaWizard";
import { InqaWizard } from "../modes/inqa/InqaWizard";
import { QmWizard } from "../modes/qm/QmWizard";
import { CustomWizard } from "../modes/custom/CustomWizard";

const STORAGE_KEY = "zww-agent.currentMode";

function loadInitialMode(): AgentMode {
  if (!appConfig.showModeSelector) return appConfig.defaultMode;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (appConfig.enabledModes as string[]).includes(raw)) return raw as AgentMode;
  } catch {
    // localStorage may be unavailable (incognito, sandbox); fall back
  }
  return appConfig.defaultMode;
}

interface Props {
  onLogout: () => void;
  showLogout: boolean;
}

export function AppShell({ onLogout, showLogout }: Props) {
  const [mode, setMode] = useState<AgentMode>(loadInitialMode);

  useEffect(() => {
    document.title = appConfig.title;
  }, []);

  useEffect(() => {
    if (!appConfig.showModeSelector) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, [mode]);

  return (
    <>
      <header className="header">
        <h1>{appConfig.title}</h1>
        <div className="controls">
          <ModelPicker />
          {showLogout && (
            <button type="button" className="btn btn-secondary" onClick={onLogout}>
              Abmelden
            </button>
          )}
        </div>
      </header>

      <main className="main split-view">
        <aside className="left-panel">
          {appConfig.showModeSelector && (
            <ModeSelector
              current={mode}
              enabled={appConfig.enabledModes}
              onChange={setMode}
            />
          )}
          {mode === "bafa" && <BafaWizard />}
          {mode === "inqa" && <InqaWizard />}
          {mode === "qm_handbuch" && <QmWizard />}
          {mode === "custom_document" && <CustomWizard />}
        </aside>
        <aside className="right-panel">
          <ChatPanel mode={mode} enabled />
        </aside>
      </main>
    </>
  );
}
